import {Injectable} from '@angular/core';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {BuggyForProductionSchedule} from '@domain/production-schedule/buggy-for-production-schedule';
import {
  AgGridUtils,
  ColDefBuilderFactoryService,
  ContentSwitcherNavigationInput,
  CrudColdef,
  GridOptionsBuilder,
  LinkIconRendererComponent,
  NoDataOverlayComponentParams,
  OverlayComponentParams,
  StringUtils,
  TranslateService,
  Unit
} from '@vdw/angular-component-library';
import {ICellRendererParams, ValueGetterParams} from 'ag-grid-community';
import {L10nIntlService} from 'angular-l10n';
import {Subject} from 'rxjs';

@Injectable({providedIn: 'root'})
export class BuggyOverviewService {
  public constructor(
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly translate: TranslateService,
    private readonly l10nIntlService: L10nIntlService
  ) {}

  public getColumnDefs(withLinkIcon: boolean = true, rowActionsComplete?: Subject<void>): CrudColdef {
    let nameColDef = this.colDefBuilderFactoryService.getBuilder().withColIdAndField('name', true).withHeaderName('GENERAL.NAME').withComparator(StringUtils.stringComparator);

    if (withLinkIcon) {
      const editRoute = NavigationUtils.getNavigationRoute(NavigationId.EDIT_BUGGY);

      nameColDef = nameColDef.withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
        return {
          params,
          editPageUrl: NavigationUtils.getAbsolutePath(editRoute.data.id),
          rowActionsComplete,
          contentSwitcherEntry: {componentInfo: {component: editRoute.component}, data: editRoute.data} as ContentSwitcherNavigationInput
        };
      });
    }

    return {
      colDefs: [
        nameColDef.build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withField('amountPlannedInPicks')
          .withNumericMultiFilter()
          .withHeaderName('ORDERBOOK.PLANNED_PICKS')
          .withValueGetter((params: ValueGetterParams<BuggyForProductionSchedule>) => +params.data.amountPlannedInPicks.toFixed(2))
          .withRightAlignment()
          .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithUnit(params.value, Unit.PICKS, this.l10nIntlService))
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withField('amountPlannedInMeter')
          .withNumericMultiFilter()
          .withHeaderName('ORDERBOOK.PLANNED_METERS')
          .withValueGetter((params: ValueGetterParams<BuggyForProductionSchedule>) => +params.data.amountPlannedInMeter.toFixed(2))
          .withRightAlignment()
          .withCellRenderer((params: ICellRendererParams) => AgGridUtils.buildAgGridCellTextWithUnit(params.value, Unit.METER, this.l10nIntlService))
          .build()
      ],
      rowActionsComplete
    } as CrudColdef;
  }

  public getDefaultGridOptionsBuilder(builder: GridOptionsBuilder): GridOptionsBuilder {
    return builder
      .withLoadingOverlay({
        scale: 0.7
      } as OverlayComponentParams)
      .withNoRowsOverlay({
        scale: 0.7,
        titleParam: this.translate.instant('PLANNING.BUGGY.BUGGY'),
        hideDescription: true
      } as NoDataOverlayComponentParams);
  }
}
